<template>
  <div class="academy-injury-summary">
    <!-- Season Selector -->
    <div class="season-selector-wrapper">
      <div class="season-selector">
        <div class="season-buttons">
          <b-button-group>
            <b-button :variant="selectedSeason === 'current' ? 'primary' : 'outline-primary'
              " @click="switchSeason('current')" :disabled="loadingLastSeason">
              <i class="bi bi-calendar-check" aria-hidden="true"></i>
              Current Season ({{ getCurrentSeason().label }})
            </b-button>
            <b-button :variant="selectedSeason === 'last' ? 'primary' : 'outline-primary'
              " @click="switchSeason('last')" :disabled="loadingLastSeason">
              <i class="bi bi-calendar3" aria-hidden="true"></i>
              <span v-if="loadingLastSeason">
                <b-spinner small></b-spinner>
                Loading...
              </span>
              <span v-else> Last Season ({{ getLastSeason().label }}) </span>
            </b-button>
          </b-button-group>
        </div>

        <!-- Season Comparison Toggle -->
        <div class="comparison-toggle-wrapper">
          <b-button :variant="comparisonMode ? 'success' : 'outline-success'" @click="toggleComparisonMode"
            class="comparison-toggle" :disabled="loadingLastSeason ||
              (selectedSeason === 'last' && lastSeasonInjuries.length === 0)
              " size="md">
            <i class="bi bi-arrow-left-right" aria-hidden="true"></i>
            <span v-if="loadingLastSeason && !comparisonMode">
              <b-spinner small></b-spinner>
              Loading...
            </span>
            <span v-else>
              {{ comparisonMode ? "Exit Comparison" : "Compare Seasons" }}
            </span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Comparison Mode Indicator -->
    <div v-if="comparisonMode" class="comparison-active-indicator">
      <i class="bi bi-check-circle text-success" aria-hidden="true"></i>
      Academy-wide season comparison active - Viewing both seasons side by side
    </div>

    <!-- Season Comparison Dashboard -->
    <div v-if="comparisonMode" class="season-comparison-dashboard">
      <div class="comparison-header">
        <h2>Academy Season Comparison Analysis</h2>
      </div>

      <!-- Comparison Metrics Summary -->
      <div class="comparison-metrics-grid">
        <div class="metric-card">
          <h4>Total Injuries</h4>
          <div class="metric-comparison">
            <div class="season-metric">
              <span class="season-label">Current</span>
              <span class="metric-value">{{ activeInjuries.length }}</span>
            </div>
            <div class="trend-indicator">
              <span :class="totalInjuryTrendClass">
                <i :class="totalInjuryTrendIcon" aria-hidden="true"></i>
                {{ totalInjuryTrendPercentage }}
              </span>
            </div>
            <div class="season-metric">
              <span class="season-label">Last</span>
              <span class="metric-value">{{ lastSeasonInjuries.length }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <h4>Recovery Rate</h4>
          <div class="metric-comparison">
            <div class="season-metric">
              <span class="season-label">Current</span>
              <span class="metric-value">{{ currentSeasonRecoveryRate }}%</span>
            </div>
            <div class="trend-indicator">
              <span :class="recoveryTrendClass">
                <i :class="recoveryTrendIcon" aria-hidden="true"></i>
                {{ recoveryTrendPercentage }}
              </span>
            </div>
            <div class="season-metric">
              <span class="season-label">Last</span>
              <span class="metric-value">{{ lastSeasonRecoveryRate }}%</span>
            </div>
          </div>
        </div>

        <div class="metric-card">
          <h4>Average Return Time</h4>
          <div class="metric-comparison">
            <div class="season-metric">
              <span class="season-label">Current</span>
              <span class="metric-value">{{ currentSeasonAvgReturnTime }} days</span>
            </div>
            <div class="trend-indicator">
              <span :class="returnTimeTrendClass">
                <i :class="returnTimeTrendIcon" aria-hidden="true"></i>
                {{ returnTimeTrendPercentage }}
              </span>
            </div>
            <div class="season-metric">
              <span class="season-label">Last</span>
              <span class="metric-value">{{ lastSeasonAvgReturnTime }} days</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Side-by-Side Summary Tables -->
      <div class="side-by-side-comparison">
        <div class="season-column">
          <h4>Current Season ({{ getCurrentSeason().label }})</h4>
          <div class="comparison-summary">
            <div class="summary-item">
              <strong>Most Common Injury:</strong> {{ currentSeasonTopInjury }}
            </div>
            <div class="summary-item">
              <strong>Most Affected Age Group:</strong>
              {{ currentSeasonTopAgeGroup }}
            </div>
            <div class="summary-item">
              <strong>Total Teams with Injuries:</strong>
              {{ currentSeasonAffectedTeams }}
            </div>
          </div>
        </div>

        <div class="season-column">
          <h4>Last Season ({{ getLastSeason().label }})</h4>
          <div class="comparison-summary">
            <div class="summary-item">
              <strong>Most Common Injury:</strong> {{ lastSeasonTopInjury }}
            </div>
            <div class="summary-item">
              <strong>Most Affected Age Group:</strong>
              {{ lastSeasonTopAgeGroup }}
            </div>
            <div class="summary-item">
              <strong>Total Teams with Injuries:</strong>
              {{ lastSeasonAffectedTeams }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="search-filter">
      <div class="form__item">
        <label class="form__label" for="picker">
          Select Injuries date range</label>
        <VueDatePicker :locale="'en-GB'" ref="picker" v-model="dateRange" range @update:model-value="dateRangeSelected"
          :format="'dd/MM/yyyy'" :enable-time-picker="false" auto-apply />
        <a class="clear-daterange" v-b-tooltip.hover title="Reset date range" @click="dateRangeReset()"
          href="javascript:;"><i class="bi bi-arrow-counterclockwise"></i></a>
        <div class="checkbox-full-season-wrapper">
          <b-form-checkbox id="checkbox-full-season" v-model="fullSeasonCheckBoxData" @change="fullSeasonCheckBox()"
            name="checkbox-full-season">
            Full Season
          </b-form-checkbox>
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="coachSelect">Select team</label>
        <select v-model="selectedTeamId" @change="selectTeamDataOnly()" id="coachSelect">
          <option disabled value="">Please select a team</option>
          <option value="all">All teams</option>
          <option v-for="team in teamData" :value="team.teamId" :key="team.teamId">
            {{ team.teamName }}
          </option>
        </select>
      </div>
    </div>

    <section class="academy-injury-summary-section-reports">
      <h2>Academy Injury Summary Report - {{ selectedSeasonLabel }}</h2>
      <div>
        Date: {{ formatDate(currentSeasonDates.startDate) }} -
        {{ formatDate(currentSeasonDates.endDate) }}
      </div>

      <h3>Table 1: Player Injury Summary</h3>
      <div class="injury-summary-table-scroll">
        <b-table :items="activeInjurySummary" :fields="summaryFields" small striped responsive sortable
          @row-clicked="handleTableRowClick" class="clickable-table">
          <!-- RTP Phase Column -->
          <template #cell(rtp_phase)="data">
            <span v-if="data.value" class="rtp-badge" :class="'rtp-phase-' + data.value">
              P{{ data.value }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <button v-if="!data.item.rtp_phase && data.item.injury_status !== 'Fully recoverd'"
              class="btn btn-sm btn-outline-primary" @click.stop="openStartRTPModal(data.item)"
              title="Start RTP Protocol">
              <i class="bi bi-activity"></i> Start RTP
            </button>
            <button v-else-if="data.item.rtp_phase" class="btn btn-sm btn-outline-success"
              @click.stop="viewRTPDetails(data.item)" title="View RTP Progress">
              <i class="bi bi-eye"></i> View
            </button>
          </template>
        </b-table>
      </div>

      <h3>Table 2: Injury Types by Age Group</h3>
      <b-table :items="activeInjuryByTeam" :fields="injuryByTeamFields" small striped responsive sortable
        @row-clicked="handleInjuryByTeamRowClick" class="clickable-table"></b-table>

      <h3>Table 3: Top Injury Types (All Players)</h3>
      <b-table :items="activeTopInjuryTypes" :fields="topInjuryFields" small striped responsive sortable
        @row-clicked="handleTopInjuryTypeRowClick" class="clickable-table"></b-table>

      <h3>Table 4: Activity at Time of Injury</h3>
      <b-table :items="activeActivityAtTimeTable" :fields="activityAtTimeFields" small striped responsive sortable
        @row-clicked="handleActivityTableRowClick" class="clickable-table"></b-table>
    </section>

    <section class="academy-injury-summary-section-charts academy-injury-summary-section-reports">
      <h2>Injury Trend Charts</h2>

      <!-- View Mode Controls -->
      <div class="chart-view-controls">
        <div class="view-mode-section">
          <label class="view-mode-label">Time Period View:</label>
          <div class="view-mode-buttons">
            <button v-for="viewOption in viewModeOptions" :key="viewOption.value"
              @click="selectedViewMode = viewOption.value" :class="{ active: selectedViewMode === viewOption.value }"
              class="view-mode-btn">
              {{ viewOption.label }}
            </button>
          </div>
        </div>
      </div>

      <InjuryTrendChart :injuries="activeInjuries" :date-range="activeDateRange" :view-mode="selectedViewMode"
        @drill-down="handleDrillDown" @view-mode-change="selectedViewMode = $event" />
    </section>

    <section class="academy-injury-summary-section-charts academy-injury-summary-section-reports">
      <h2>Injury Heat Map</h2>
      <p class="text-muted">
        Click on the heat map to view detailed injury data for each team.
      </p>

      <injury-heat-map v-if="teamData.length > 0" :injuries="injuries" :teams="teamData" :view-mode="selectedViewMode"
        :date-range="dateRange" @cell-click="handleHeatMapDrillDown" />
    </section>

    <section class="academy-injury-summary-section-modal">
      <InjuryDrillDownModal v-if="selectedInjury" :drill-down-data="selectedInjury" @close="selectedInjury = null"
        @view-full-report="viewFullReport" @export-data="exportData" />
    </section>

    <!-- Start RTP Protocol Modal -->
    <b-modal v-model="showStartRTPModal" title="Start Return-to-Play Protocol" size="md" @ok.prevent="startRTPProtocol"
      ok-title="Start RTP Protocol" ok-variant="primary" :ok-disabled="startingRTP">
      <div v-if="selectedInjuryForRTP" class="start-rtp-form">
        <div class="player-info-header mb-3">
          <strong>{{ selectedInjuryForRTP.player_name }}</strong>
          <span class="text-muted ms-2">{{ selectedInjuryForRTP.team }}</span>
        </div>

        <div class="injury-info mb-3 p-2 bg-light rounded">
          <div><strong>Injury:</strong> {{ selectedInjuryForRTP.injury_type }}</div>
          <div><strong>Date:</strong> {{ selectedInjuryForRTP.created_date }}</div>
          <div><strong>Status:</strong> {{ selectedInjuryForRTP.injury_status }}</div>
        </div>

        <div class="form-group mb-3">
          <label class="form-label">Target Return Date</label>
          <input type="date" v-model="rtpForm.targetDate" class="form-control"
            :min="new Date().toISOString().split('T')[0]" />
          <small class="text-muted">Optional - estimated return date</small>
        </div>

        <div class="form-group mb-3">
          <label class="form-label">Initial Restrictions</label>
          <textarea v-model="rtpForm.restrictions" class="form-control" rows="3"
            placeholder="e.g., No contact training, limited cardio..."></textarea>
        </div>

        <div class="alert alert-info">
          <i class="bi bi-info-circle me-2"></i>
          Player will start at <strong>Phase 1 (Acute/Rest)</strong>.
          Progress through phases requires sign-off from appropriate staff.
        </div>
      </div>
    </b-modal>

    <!-- View RTP Details Modal -->
    <b-modal v-model="showRTPDetailsModal"
      :title="selectedInjuryForRTP ? selectedInjuryForRTP.player_name + ' - RTP Progress' : 'RTP Details'" size="xl"
      hide-footer>
      <RTPPhaseTracker v-if="showRTPDetailsModal && selectedInjuryForRTP && selectedInjuryForRTP.id"
        :injuryId="selectedInjuryForRTP.id" :userData="userData" @phase-updated="onRTPPhaseUpdated"
        @rtp-completed="onRTPCompleted" />
    </b-modal>

    <section class="academy-injury-summary-section-distribution academy-injury-summary-section-reports">
      <h2>Injury Type Distribution</h2>
      <p class="text-muted">
        Distribution of injury types across all players. Click on chart segments
        or legend items for detailed information.
      </p>
      <injury-type-distribution-chart :injuries="injuries" :teams="teamData" :players="allPlayersData"
        @segment-click="handleDistributionDrillDown" />
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Teams } from "@/services/teams-service.js";
import { Users } from "@/services/user-service.js";
import { PlayerInjury } from "@/services/player-Injury-service.js";
import { ReturnToPlayService } from "@/services/rtp-service.js";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import removeDuplicatesMixin from "@/mixin/removeDuplicates.js";
import InjuryTrendChart from "./InjuryTrendChart";
import InjuryHeatMap from "./InjuryHeatMap.vue";
import InjuryTypeDistributionChart from "./InjuryTypeDistributionChart.vue";
import InjuryDrillDownModal from "./InjuryDrillDownModal.vue";
import RTPPhaseTracker from "@/components/RTPPhaseTracker/index.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import dayjs from "dayjs";
export default {
  name: "AcademyInjurySummaryReport",
  mixins: [extendSeasonEndDateToFullYearMixin, removeDuplicatesMixin],
  components: {
    VueDatePicker,
    InjuryTrendChart,
    InjuryHeatMap,
    InjuryTypeDistributionChart,
    InjuryDrillDownModal,
    RTPPhaseTracker,
  },
  data() {
    return {
      selectedTeamId: "all",
      fullSeasonCheckBoxData: true,
      playingseasonEndDate: this.extendSeasonEndDateToFullYear(),
      playingseasonStartDate: localStorage.getItem(
        "currentPlayingseasonStartDate",
      ),
      dateRange: [
        new Date(localStorage.getItem("currentPlayingseasonStartDate")),
        new Date(this.extendSeasonEndDateToFullYear()),
      ],
      allPlayersData: [],
      teamData: [],
      injuries: [],
      userData: {
        clubId: "",
        name: "",
        userType: "",
      },
      selectedInjury: null,
      selectedViewMode: "month",
      viewModeOptions: [
        { value: "day", label: "Daily" },
        { value: "week", label: "Weekly" },
        { value: "month", label: "Monthly" },
      ],
      reportDate: new Date().toISOString().slice(0, 10),
      injuryTypeGroups: [
        { label: "Ankle", values: ["Left ankle injury", "Right ankle injury"] },
        { label: "Knee", values: ["Left Knee injury", "Right Knee injury"] },
        { label: "Quad", values: ["Left Quad injury", "Right Quad injury"] },
        {
          label: "Hamstring",
          values: ["Left hamstring injury", "Right hamstring injury"],
        },
        { label: "Groin", values: ["Left groin injury", "Right groin injury"] },
        { label: "Hip", values: ["Left Hip injury", "Right Hip injury"] },
        {
          label: "Shoulder",
          values: ["Left Shoulder injury", "Right Shoulder injury"],
        },
        {
          label: "Wrist/Hand",
          values: ["Left Wrist and hand injury", "Right Wrist and hand injury"],
        },
        { label: "Head", values: ["Head injury"] },
        { label: "Foot", values: ["Left foot injury", "Right foot injury"] },
        { label: "Arm", values: ["Left arm injury", "Right arm injury"] },
        { label: "Calf", values: ["Left calf injury", "Right calf injury"] },
        { label: "Other", values: [] },
      ],
      summaryFields: [
        { key: "player_name", label: "Player Name", sortable: true },
        { key: "team", label: "Team", sortable: true },
        { key: "injury_type", label: "Injury Type", sortable: true },
        { key: "more_detail", label: "Injury Details", sortable: true },
        { key: "created_date", label: "Date of Injury", sortable: true },
        { key: "injury_status", label: "Status", sortable: true },
        { key: "rtp_phase", label: "RTP", sortable: true },
        { key: "return_time", label: "Est. Return", sortable: true },
        { key: "notes", label: "Notes", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
      injuryByTeamFields: [
        { key: "team", label: "Team", sortable: true },
        { key: "Ankle", label: "Ankle", sortable: true },
        { key: "Knee", label: "Knee", sortable: true },
        { key: "Quad", label: "Quad", sortable: true },
        { key: "Hamstring", label: "Hamstring", sortable: true },
        { key: "Groin", label: "Groin", sortable: true },
        { key: "Hip", label: "Hip", sortable: true },
        { key: "Shoulder", label: "Shoulder", sortable: true },
        { key: "Wrist/Hand", label: "Wrist/Hand", sortable: true },
        { key: "Head", label: "Head", sortable: true },
        { key: "Foot", label: "Foot", sortable: true },
        { key: "Arm", label: "Arm", sortable: true },
        { key: "Calf", label: "Calf", sortable: true },
        { key: "Other", label: "Other", sortable: true },
        { key: "Total", label: "Total Injuries", sortable: true },
      ],
      topInjuryFields: [
        { key: "injury_type", label: "Injury Type", sortable: true },
        { key: "count", label: "Number of Cases", sortable: true },
      ],
      activityAtTimeFields: [
        { key: "player_name", label: "Player Name", sortable: true },
        { key: "team", label: "Team", sortable: true },
        { key: "injury_type", label: "Injury Type", sortable: true },
        { key: "activity_at_time", label: "Activity at Time", sortable: true },
        { key: "created_date", label: "Date of Injury", sortable: true },
      ],

      // Season management properties
      selectedSeason: "current",
      comparisonMode: false,
      loadingLastSeason: false,

      // Last season data stores
      lastSeasonInjuries: [],
      lastSeasonDateRange: [],

      // RTP Modal properties
      showStartRTPModal: false,
      showRTPDetailsModal: false,
      selectedInjuryForRTP: null,
      rtpForm: {
        targetDate: "",
        restrictions: "",
      },
      startingRTP: false,
    };
  },
  mounted() {
    if (this.getProfile !== null) {
      this.userData = this.getProfile;
    }

    Promise.resolve(this.populateTeamData()).then(() => {
      this.getInjuryedPlayersByClubId();
    });
  },
  computed: {
    ...mapGetters(["getProfile"]),
    activityAtTimeTable() {
      return this.injuries.map((i) => ({
        player_name: this.getPlayerName(i.player_id) || i.player_id,
        team: this.getTeamName(i.teamId) || "Unknown",
        injury_type: i.injury_type,
        activity_at_time: i.activity_at_time || "",
        created_date: i.created_date ? i.created_date.slice(0, 10) : "",
      }));
    },
    injurySummary() {
      // Map injuries to summary table format
      return this.injuries.map((i) => ({
        id: i.id,
        player_id: i.player_id,
        player_name: this.getPlayerName(i.player_id) || i.player_id, // adjust as needed
        team: this.getTeamName(i.teamId) || "Unknown",
        injury_type: i.injury_type,
        more_detail: i.more_detail,
        created_date: i.created_date ? i.created_date.slice(0, 10) : "",
        injury_status: i.injury_status,
        rtp_phase: i.rtp_phase,
        return_time: i.return_time ? i.return_time.slice(0, 10) : "",
        notes: i.treatment || i.diagnosis || "",
      }));
    },
    injuryByTeam() {
      const teams = {};
      const types = this.injuryTypeGroups.map((g) => g.label);
      this.injuries.forEach((i) => {
        const team = this.getTeamName(i.teamId) || "Unknown";
        if (!teams[team]) {
          teams[team] = { team, Total: 0 };
          types.forEach((t) => (teams[team][t] = 0));
        }
        let type = this.mapInjuryType(i.injury_type);
        teams[team][type]++;
        teams[team].Total++;
      });
      // Add total row
      const total = { team: "Total", Total: 0 };
      types.forEach((t) => (total[t] = 0));
      Object.values(teams).forEach((row) => {
        types.forEach((t) => (total[t] += row[t]));
        total.Total += row.Total;
      });
      return [...Object.values(teams), total];
    },
    topInjuryTypes() {
      const counts = {};
      const types = [
        "Ankle",
        "Knee",
        "Quad",
        "Hamstring",
        "Groin",
        "Hip",
        "Shoulder",
        "Wrist/Hand",
        "Head",
        "Foot",
        "Arm",
        "Calf",
        "Other",
      ];
      this.injuries.forEach((i) => {
        let type = this.mapInjuryType(i.injury_type);
        counts[type] = (counts[type] || 0) + 1;
      });
      // Only show types that have at least one case
      return types
        .map((type) => ({ injury_type: type, count: counts[type] || 0 }))
        .filter((row) => row.count > 0);
    },

    // Season management computed properties
    selectedSeasonLabel() {
      return this.selectedSeason === "current"
        ? this.getCurrentSeason().label
        : this.getLastSeason().label;
    },

    currentSeasonDates() {
      return this.selectedSeason === "current"
        ? this.getCurrentSeason()
        : this.getLastSeason();
    },

    // Dynamic data getters based on selected season
    activeInjuries() {
      return this.selectedSeason === "current"
        ? this.injuries
        : this.lastSeasonInjuries;
    },

    activeDateRange() {
      return this.selectedSeason === "current"
        ? this.dateRange
        : this.lastSeasonDateRange;
    },

    activeInjurySummary() {
      const activeInjuries = this.activeInjuries;
      return activeInjuries.map((i) => ({
        id: i.id,
        player_id: i.player_id,
        player_name: this.getPlayerName(i.player_id) || i.player_id,
        team: this.getTeamName(i.teamId) || "Unknown",
        injury_type: i.injury_type,
        more_detail: i.more_detail,
        created_date: i.created_date ? i.created_date.slice(0, 10) : "",
        injury_status: i.injury_status,
        rtp_phase: i.rtp_phase,
        return_time: i.return_time ? i.return_time.slice(0, 10) : "",
        notes: i.treatment || i.diagnosis || "",
      }));
    },

    activeInjuryByTeam() {
      const teams = {};
      const types = this.injuryTypeGroups.map((g) => g.label);
      const activeInjuries = this.activeInjuries;

      activeInjuries.forEach((i) => {
        const team = this.getTeamName(i.teamId) || "Unknown";
        if (!teams[team]) {
          teams[team] = { team, Total: 0 };
          types.forEach((t) => (teams[team][t] = 0));
        }
        let type = this.mapInjuryType(i.injury_type);
        teams[team][type]++;
        teams[team].Total++;
      });

      const total = { team: "Total", Total: 0 };
      types.forEach((t) => (total[t] = 0));
      Object.values(teams).forEach((row) => {
        types.forEach((t) => (total[t] += row[t]));
        total.Total += row.Total;
      });
      return [...Object.values(teams), total];
    },

    activeTopInjuryTypes() {
      const counts = {};
      const types = this.injuryTypeGroups.map((g) => g.label);
      const activeInjuries = this.activeInjuries;

      activeInjuries.forEach((i) => {
        let type = this.mapInjuryType(i.injury_type);
        counts[type] = (counts[type] || 0) + 1;
      });

      return types
        .map((type) => ({ injury_type: type, count: counts[type] || 0 }))
        .filter((row) => row.count > 0);
    },

    activeActivityAtTimeTable() {
      const activeInjuries = this.activeInjuries;
      return activeInjuries.map((i) => ({
        player_name: this.getPlayerName(i.player_id) || i.player_id,
        team: this.getTeamName(i.teamId) || "Unknown",
        injury_type: i.injury_type,
        activity_at_time: i.activity_at_time || "",
        created_date: i.created_date ? i.created_date.slice(0, 10) : "",
      }));
    },

    // Comparison analytics
    totalInjuryTrendPercentage() {
      const current = this.injuries.length;
      const last = this.lastSeasonInjuries.length;
      if (last === 0) return current > 0 ? "+100%" : "0%";
      const change = ((current - last) / last) * 100;
      return change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
    },

    totalInjuryTrendClass() {
      const current = this.injuries.length;
      const last = this.lastSeasonInjuries.length;
      return current < last
        ? "trend-better"
        : current > last
          ? "trend-worse"
          : "trend-same";
    },

    totalInjuryTrendIcon() {
      const current = this.injuries.length;
      const last = this.lastSeasonInjuries.length;
      return current < last
        ? "bi-arrow-down"
        : current > last
          ? "bi-arrow-up"
          : "bi-arrow-right";
    },

    currentSeasonRecoveryRate() {
      const total = this.injuries.length;
      if (total === 0) return 0;

      console.log("=== RECOVERY RATE DEBUG ===");
      console.log("Total current season injuries:", total);

      // Debug: Check what statuses exist
      const allStatuses = this.injuries
        .map((i) => i.injury_status)
        .filter((s) => s);
      console.log("All current season statuses:", [...new Set(allStatuses)]);

      // Debug: Check how many have return_time
      const withReturnTime = this.injuries.filter(
        (i) => i.return_time && i.return_time.trim() !== "",
      ).length;
      console.log("Injuries with return_time:", withReturnTime);

      // For recovery rate, consider injuries that have:
      // 1. "Fully recoverd" status (exact match to DB typo)
      // 2. OR injuries in recovery phase with return times (indicating treatment is progressing)
      const recovered = this.injuries.filter((i) => {
        const status = i.injury_status
          ? i.injury_status.toLowerCase().trim()
          : "";

        // Count as recovered if:
        // 1. Explicitly marked as fully recovered
        const isFullyRecovered =
          status === "fully recoverd" || // Note: keeping the typo as it exists in DB
          status === "fully recovered" ||
          status.includes("light training") ||
          status.includes("return to fitness");

        // 2. OR has return time estimate (indicating active treatment/recovery)
        const hasRecoveryPlan = i.return_time && i.return_time.trim() !== "";

        const isRecovered = isFullyRecovered || hasRecoveryPlan;

        if (isRecovered) {
          console.log("Found injury in recovery:", {
            status: i.injury_status,
            return_time: i.return_time,
            reason: isFullyRecovered ? "Fully recovered" : "Has recovery plan",
          });
        }

        return isRecovered;
      }).length;

      console.log("Recovered injuries count:", recovered);
      const rate = Math.round((recovered / total) * 100);
      console.log("Recovery rate:", rate + "%");

      return rate;
    },

    lastSeasonRecoveryRate() {
      const total = this.lastSeasonInjuries.length;
      if (total === 0) return 0;

      console.log("=== LAST SEASON RECOVERY RATE DEBUG ===");
      console.log("Total last season injuries:", total);

      // Debug: Check what statuses exist
      const allStatuses = this.lastSeasonInjuries
        .map((i) => i.injury_status)
        .filter((s) => s);
      console.log("All last season statuses:", [...new Set(allStatuses)]);

      // Use the actual injury status values from the database
      // Only count truly recovered statuses, not just having return_time
      const recovered = this.lastSeasonInjuries.filter((i) => {
        const status = i.injury_status
          ? i.injury_status.toLowerCase().trim()
          : "";

        // Only count actual recovery statuses, be more strict
        return (
          status === "fully recoverd" || // Note: keeping the typo as it exists in DB
          status === "fully recovered" ||
          status.includes("light training") ||
          status.includes("return to fitness")
        );
      }).length;

      console.log("Last season recovered injuries count:", recovered);
      const rate = Math.round((recovered / total) * 100);
      console.log("Last season recovery rate:", rate + "%");

      return rate;
    },
    recoveryTrendPercentage() {
      const current = this.currentSeasonRecoveryRate;
      const last = this.lastSeasonRecoveryRate;
      if (last === 0) return current > 0 ? "+100%" : "0%";
      const change = current - last;
      return change > 0 ? `+${change}%` : `${change}%`;
    },

    recoveryTrendClass() {
      const current = this.currentSeasonRecoveryRate;
      const last = this.lastSeasonRecoveryRate;
      return current > last
        ? "trend-better"
        : current < last
          ? "trend-worse"
          : "trend-same";
    },

    recoveryTrendIcon() {
      const current = this.currentSeasonRecoveryRate;
      const last = this.lastSeasonRecoveryRate;
      return current > last
        ? "bi-arrow-up"
        : current < last
          ? "bi-arrow-down"
          : "bi-arrow-right";
    },

    currentSeasonAvgReturnTime() {
      console.log("=== RETURN TIME DEBUG ===");
      console.log("Total current season injuries:", this.injuries.length);

      // Check all injuries with return_time first
      const withReturnTime = this.injuries.filter((i) => {
        return i.return_time && i.return_time.trim() !== "";
      });
      console.log("Injuries with any return_time:", withReturnTime.length);

      if (withReturnTime.length > 0) {
        console.log(
          "Sample return_time values:",
          withReturnTime.slice(0, 3).map((i) => ({
            return_time: i.return_time,
            created_date: i.created_date,
            status: i.injury_status,
          })),
        );
      }

      // Use any injury with return_time
      const injuriesWithReturnTime = this.injuries.filter((i) => {
        return i.return_time && i.return_time.trim() !== "";
      });

      console.log(
        "Injuries for return time calculation:",
        injuriesWithReturnTime.length,
      );

      if (injuriesWithReturnTime.length === 0) {
        console.log("No injuries with return_time found, returning 0");
        return 0;
      }

      let validCalculations = 0;
      const totalDays = injuriesWithReturnTime.reduce((sum, injury) => {
        const days = this.convertReturnTimeTextToDays(injury.return_time);

        console.log("Converting return time:", {
          return_time: injury.return_time,
          converted_days: days,
          player_id: injury.player_id,
        });

        if (days > 0) {
          validCalculations++;
          return sum + days;
        }

        return sum;
      }, 0);

      console.log(
        "Total days sum:",
        totalDays,
        "Valid calculations:",
        validCalculations,
      );

      const avgDays =
        validCalculations > 0 ? Math.round(totalDays / validCalculations) : 0;
      console.log("Average return time:", avgDays, "days");
      return avgDays;
    },
    lastSeasonAvgReturnTime() {
      console.log("=== LAST SEASON RETURN TIME DEBUG ===");
      console.log(
        "Total last season injuries:",
        this.lastSeasonInjuries.length,
      );

      // Check all injuries with return_time first
      const withReturnTime = this.lastSeasonInjuries.filter((i) => {
        return i.return_time && i.return_time.trim() !== "";
      });
      console.log(
        "Last season injuries with any return_time:",
        withReturnTime.length,
      );

      // Use any injury with return_time
      const injuriesWithReturnTime = this.lastSeasonInjuries.filter((i) => {
        return i.return_time && i.return_time.trim() !== "";
      });

      console.log(
        "Last season injuries for return time calculation:",
        injuriesWithReturnTime.length,
      );

      if (injuriesWithReturnTime.length === 0) return 0;

      let validCalculations = 0;
      const totalDays = injuriesWithReturnTime.reduce((sum, injury) => {
        const days = this.convertReturnTimeTextToDays(injury.return_time);

        if (days > 0) {
          validCalculations++;
          return sum + days;
        }

        return sum;
      }, 0);

      const avgDays =
        validCalculations > 0 ? Math.round(totalDays / validCalculations) : 0;
      console.log("Last season average return time:", avgDays, "days");
      return avgDays;
    },
    returnTimeTrendPercentage() {
      const current = this.currentSeasonAvgReturnTime;
      const last = this.lastSeasonAvgReturnTime;
      if (last === 0) return current > 0 ? "+100%" : "0%";
      const change = ((current - last) / last) * 100;
      return change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
    },

    returnTimeTrendClass() {
      const current = this.currentSeasonAvgReturnTime;
      const last = this.lastSeasonAvgReturnTime;
      return current < last
        ? "trend-better"
        : current > last
          ? "trend-worse"
          : "trend-same";
    },

    returnTimeTrendIcon() {
      const current = this.currentSeasonAvgReturnTime;
      const last = this.lastSeasonAvgReturnTime;
      return current < last
        ? "bi-arrow-down"
        : current > last
          ? "bi-arrow-up"
          : "bi-arrow-right";
    },

    // Comparison summary data
    currentSeasonTopInjury() {
      if (this.injuries.length === 0) return "None";
      const counts = {};
      this.injuries.forEach((i) => {
        const type = this.mapInjuryType(i.injury_type);
        counts[type] = (counts[type] || 0) + 1;
      });
      return Object.keys(counts).reduce((a, b) =>
        counts[a] > counts[b] ? a : b,
      );
    },

    lastSeasonTopInjury() {
      if (this.lastSeasonInjuries.length === 0) return "None";
      const counts = {};
      this.lastSeasonInjuries.forEach((i) => {
        const type = this.mapInjuryType(i.injury_type);
        counts[type] = (counts[type] || 0) + 1;
      });
      return Object.keys(counts).reduce((a, b) =>
        counts[a] > counts[b] ? a : b,
      );
    },

    currentSeasonTopAgeGroup() {
      if (this.injuries.length === 0) return "None";
      const counts = {};
      this.injuries.forEach((i) => {
        const team = this.getTeamName(i.teamId) || "Unknown";
        counts[team] = (counts[team] || 0) + 1;
      });
      return Object.keys(counts).reduce((a, b) =>
        counts[a] > counts[b] ? a : b,
      );
    },

    lastSeasonTopAgeGroup() {
      if (this.lastSeasonInjuries.length === 0) return "None";
      const counts = {};
      this.lastSeasonInjuries.forEach((i) => {
        const team = this.getTeamName(i.teamId) || "Unknown";
        counts[team] = (counts[team] || 0) + 1;
      });
      return Object.keys(counts).reduce((a, b) =>
        counts[a] > counts[b] ? a : b,
      );
    },

    currentSeasonAffectedTeams() {
      const teams = new Set();
      this.injuries.forEach((i) => {
        const team = this.getTeamName(i.teamId);
        if (team) teams.add(team);
      });
      return teams.size;
    },

    lastSeasonAffectedTeams() {
      const teams = new Set();
      this.lastSeasonInjuries.forEach((i) => {
        const team = this.getTeamName(i.teamId);
        if (team) teams.add(team);
      });
      return teams.size;
    },
  },
  methods: {
    // RTP Protocol Methods
    openStartRTPModal(injury) {
      this.selectedInjuryForRTP = injury;
      this.rtpForm = {
        targetDate: "",
        restrictions: "",
      };
      this.showStartRTPModal = true;
    },

    viewRTPDetails(injury) {
      this.selectedInjuryForRTP = injury;
      this.showRTPDetailsModal = true;
    },

    async startRTPProtocol() {
      if (!this.selectedInjuryForRTP) return;

      this.startingRTP = true;
      try {
        const params = {
          injury_id: this.selectedInjuryForRTP.id,
          target_date: this.rtpForm.targetDate || null,
          restrictions: this.rtpForm.restrictions || null,
        };

        const response = await ReturnToPlayService.startRTPProtocol(params);
        const result = response.data || response;

        if (result.success) {
          this.$toast.info("RTP Protocol started successfully!", {
            title: "Success",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          });
          this.showStartRTPModal = false;
          // Refresh injury data to show updated RTP phase
          this.getInjuryedPlayersByClubId();
        } else {
          this.$toast.error(result.message || "Failed to start RTP protocol", {
            title: "Error",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
          });
        }
      } catch (error) {
        console.error("Error starting RTP protocol:", error);
        this.$toast.error("An error occurred while starting RTP protocol", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      } finally {
        this.startingRTP = false;
      }
    },

    onRTPPhaseUpdated() {
      // Refresh injury data when RTP phase is updated
      this.getInjuryedPlayersByClubId();
    },

    onRTPCompleted() {
      this.showRTPDetailsModal = false;
      this.$toast.info("RTP Protocol completed! Player cleared for full activity.", {
        title: "Congratulations",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });
      this.getInjuryedPlayersByClubId();
    },

    // Season utility methods
    getCurrentSeason() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const isAfterAugust = now.getMonth() >= 7; // August is month 7 (0-indexed)

      return {
        startYear: isAfterAugust ? currentYear : currentYear - 1,
        endYear: isAfterAugust ? currentYear + 1 : currentYear,
        label: isAfterAugust
          ? `${currentYear}/${currentYear + 1}`
          : `${currentYear - 1}/${currentYear}`,
        startDate: `${isAfterAugust ? currentYear : currentYear - 1}-08-01`,
        endDate: `${isAfterAugust ? currentYear + 1 : currentYear}-05-30`,
      };
    },

    getLastSeason() {
      const current = this.getCurrentSeason();
      return {
        startYear: current.startYear - 1,
        endYear: current.endYear - 1,
        label: `${current.startYear - 1}/${current.endYear - 1}`,
        startDate: `${current.startYear - 1}-08-01`,
        endDate: `${current.endYear - 1}-05-30`,
      };
    },

    // Helper method to convert return time text to days
    convertReturnTimeTextToDays(returnTimeText) {
      if (!returnTimeText || typeof returnTimeText !== "string") return 0;

      const text = returnTimeText.toLowerCase().trim();

      // Map common return time descriptions to days
      if (text.includes("1 - 2 days") || text.includes("1-2 days")) return 1.5;
      if (text.includes("3 - 5 days") || text.includes("3-5 days")) return 4;
      if (text.includes("1 week")) return 7;
      if (text.includes("2 - 3 weeks") || text.includes("2-3 weeks"))
        return 17.5; // Average of 2-3 weeks
      if (text.includes("a month") || text.includes("1 month")) return 30;
      if (text.includes("a few months") || text.includes("few months"))
        return 90; // ~3 months

      // Additional patterns
      if (text.includes("2 weeks")) return 14;
      if (text.includes("3 weeks")) return 21;
      if (text.includes("4 weeks")) return 28;
      if (text.includes("2 months")) return 60;
      if (text.includes("3 months")) return 90;

      console.log("Unknown return time pattern:", returnTimeText);
      return 0; // Unknown pattern
    },

    async switchSeason(season) {
      this.selectedSeason = season;

      if (season === "last" && this.lastSeasonInjuries.length === 0) {
        this.loadingLastSeason = true;
        await this.loadLastSeasonData();
        this.loadingLastSeason = false;
      }

      // Update date range for display
      if (season === "current") {
        const current = this.getCurrentSeason();
        this.dateRange = [
          new Date(current.startDate),
          new Date(current.endDate),
        ];
      } else {
        const last = this.getLastSeason();
        this.lastSeasonDateRange = [
          new Date(last.startDate),
          new Date(last.endDate),
        ];
      }
    },

    async loadLastSeasonData() {
      const lastSeason = this.getLastSeason();

      try {
        // Use existing method but filter by season dates
        const response = await PlayerInjury.find({
          action: "getInjuryedPlayersAllByClubId",
          clubId: this.userData.clubId,
          season_start_date: lastSeason.startDate,
          season_end_date: lastSeason.endDate,
        });

        if (response.data) {
          this.lastSeasonInjuries = response.data;
          this.lastSeasonDateRange = [
            new Date(lastSeason.startDate),
            new Date(lastSeason.endDate),
          ];
        }
      } catch (error) {
        console.error("Error loading last season data:", error);
        this.$toast.error("Failed to load last season data");
      }
    },

    async toggleComparisonMode() {
      this.comparisonMode = !this.comparisonMode;

      // If entering comparison mode, ensure last season data is loaded
      if (this.comparisonMode && this.lastSeasonInjuries.length === 0) {
        this.loadingLastSeason = true;
        await this.loadLastSeasonData();
        this.loadingLastSeason = false;
      }
    },

    selectTeamDataOnly() {
      console.log("Selected team ID:", this.selectedTeamId);
      if (this.selectedTeamId === "all") {
        this.getInjuryedPlayersByClubId();
      } else {
        this.injuries = this.injuries.filter(
          (i) => i.teamId === this.selectedTeamId,
        );
        this.getInjuryedPlayersByTeamId(this.selectedTeamId);
      }
    },
    async fullSeasonCheckBox() {
      if (this.fullSeasonCheckBoxData === true) {
        this.playingseasonStartDate = localStorage.getItem(
          "currentPlayingseasonStartDate",
        );
        this.playingseasonEndDate = this.extendSeasonEndDateToFullYear();
        this.getInjuryedPlayersByClubId();
      } else {
        if (this.dateRange && this.dateRange.length === 2) {
          this.playingseasonStartDate = this.formatDate(this.dateRange[0]);
          this.playingseasonEndDate = this.formatDate(this.dateRange[1]);
          this.getInjuryedPlayersByClubId();
        }
      }
    },
    dateRangeSelected() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.playingseasonStartDate = this.formatDate(this.dateRange[0]);
        this.playingseasonEndDate = this.formatDate(this.dateRange[1]);
        this.getInjuryedPlayersByClubId();
        this.fullSeasonCheckBoxData = false; // Reset checkbox when date range is selected
      }
    },
    dateRangeReset() {
      this.dateRange = [
        new Date(localStorage.getItem("currentPlayingseasonStartDate")),
        new Date(this.extendSeasonEndDateToFullYear()),
      ];
      this.fullSeasonCheckBoxData = true;
    },
    formatDate(date) {
      return date ? dayjs(date).format("YYYY-MM-DD") : "";
    },
    getPlayerName(playerId) {
      console.log("Fetching player name for ID:", playerId);
      const player = this.allPlayersData.find((p) => p.id === playerId);
      return player ? player.name : "Unknown Player";
    },
    getTeamName(teamId) {
      const team = this.teamData.find((t) => t.teamId === teamId);
      return team ? team.teamName : "Unknown";
    },
    async getAllUsersForClubId() {
      const userData = {
        clubId: this.userData.clubId,
        action: "getUserByClubId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      return responce && responce.data && responce.data.length > 0
        ? responce.data
        : [];
    },
    async populateTeamData() {
      const teamData = {
        clubId: this.userData.clubId,
        action: "populateTeamData",
      };

      const response = await Teams.find(teamData).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        return (this.teamData = response.data);
      } else {
        return (this.teamData = []);
      }
    },
    mapInjuryType(detail) {
      if (!detail) return "Other";
      for (const group of this.injuryTypeGroups) {
        if (
          group.values.some((val) => val.toLowerCase() === detail.toLowerCase())
        ) {
          return group.label;
        }
      }
      return "Other";
    },
    async getInjuryedPlayersByTeamId(teamId) {
      if (!this.userData.clubId) {
        console.error("Club ID is not set in userData.");
        return;
      }
      const userData = {
        clubId: this.userData.clubId,
        teamId: teamId,
        action: "getInjuryedPlayersByTeamId",
        season_start_date: this.playingseasonStartDate,
        season_end_date: this.playingseasonEndDate,
      };
      const responce = await PlayerInjury.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce && responce.data && responce.data.length > 0) {
        this.injuries = responce.data;
      } else {
        this.injuries = [];
      }
    },
    async getInjuryedPlayersByClubId() {
      if (!this.userData.clubId) {
        console.error("Club ID is not set in userData.");
        return;
      }
      const userData = {
        clubId: this.userData.clubId,
        action: "getInjuryedPlayersAllByClubId",
        season_start_date: this.playingseasonStartDate,
        season_end_date: this.playingseasonEndDate,
      };
      const responce = await PlayerInjury.find(userData).catch((error) => {
        console.log(error);
      });

      // Fetch teams and players first
      const [teamData, allPlayersData] = await Promise.all([
        this.populateTeamData(),
        this.getAllUsersForClubId(),
      ]);

      this.teamData = teamData;
      this.allPlayersData = allPlayersData;

      // Now update injuries last, so computed properties have all data
      if (responce && responce.data && responce.data.length > 0) {
        this.injuries = responce.data;
      } else {
        this.injuries = [];
      }
    },

    handleDrillDown(drillDownData) {
      console.log("Drill down triggered with data:", drillDownData);
      // Add type and filtered injuries to help the modal determine context
      this.selectedInjury = {
        ...drillDownData,
        type: "trend",
        injuries: this.filterInjuriesByWeek(drillDownData.week),
        context: `Injuries during the week of ${this.formatWeekForDisplay(drillDownData.week)}`,
      };
    },

    handleHeatMapDrillDown(drillDownData) {
      console.log("Heat map drill down triggered with data:", drillDownData);
      // Set the selected injury data for the modal with filtered injuries
      this.selectedInjury = {
        ...drillDownData,
        type: "heatmap",
        injuries: this.filterInjuriesByTeamAndPeriod(
          drillDownData.teamId,
          drillDownData.period,
        ),
        context: `Injuries for ${drillDownData.teamName} during ${this.formatPeriodForDisplay(drillDownData.period)}`,
      };
    },

    handleDistributionDrillDown(drillDownData) {
      console.log(
        "Distribution drill down triggered with data:",
        drillDownData,
      );
      // Set the selected injury data for the modal with filtered injuries
      this.selectedInjury = {
        ...drillDownData,
        type: "distribution",
        injuries: this.filterInjuriesByType(drillDownData.injuryType),
        context: `All ${drillDownData.injuryType} injuries across all teams and periods`,
      };
    },

    handleTableRowClick(injury) {
      console.log("Table row clicked:", injury);
      // Find the original injury record
      const originalInjury = this.injuries.find(
        (i) =>
          i.player_id === injury.player_id &&
          i.injury_type === injury.injury_type,
      );

      this.selectedInjury = {
        type: "table",
        injuryType: injury.injury_type,
        count: 1,
        playerName: injury.player_name,
        playerId: injury.player_id,
        teamName: injury.team,
        teamId: originalInjury?.teamId,
        injuries: originalInjury ? [originalInjury] : [],
        context: `Individual injury record for ${injury.player_name}`,
        createdDate: injury.created_date,
        status: injury.injury_status,
        returnTime: injury.return_time,
      };
    },

    handleInjuryByTeamRowClick(teamRow) {
      console.log("Injury by team row clicked:", teamRow);
      if (teamRow.team === "Total") return; // Don't drill down on total row

      const teamInjuries = this.injuries.filter(
        (injury) => this.getTeamName(injury.teamId) === teamRow.team,
      );

      this.selectedInjury = {
        type: "team-summary",
        teamName: teamRow.team,
        count: teamRow.Total,
        injuries: teamInjuries,
        context: `All injuries for ${teamRow.team} team`,
        injuryBreakdown: this.getInjuryBreakdownForTeam(teamInjuries),
      };
    },

    handleTopInjuryTypeRowClick(injuryTypeRow) {
      console.log("Top injury type row clicked:", injuryTypeRow);
      if (injuryTypeRow.count === 0) return; // Don't drill down on zero count

      this.selectedInjury = {
        type: "injury-type-summary",
        injuryType: injuryTypeRow.injury_type,
        count: injuryTypeRow.count,
        injuries: this.filterInjuriesByType(injuryTypeRow.injury_type),
        context: `All ${injuryTypeRow.injury_type} injuries across all teams`,
      };
    },

    handleActivityTableRowClick(activityRow) {
      console.log("Activity table row clicked:", activityRow);
      // Find the original injury record
      const originalInjury = this.injuries.find(
        (i) =>
          i.player_id === activityRow.player_id ||
          (this.getPlayerName(i.player_id) === activityRow.player_name &&
            i.injury_type === activityRow.injury_type),
      );

      this.selectedInjury = {
        type: "activity-detail",
        injuryType: activityRow.injury_type,
        count: 1,
        playerName: activityRow.player_name,
        teamName: activityRow.team,
        activityAtTime: activityRow.activity_at_time,
        injuries: originalInjury ? [originalInjury] : [],
        context: `Activity-related injury for ${activityRow.player_name}`,
        createdDate: activityRow.created_date,
      };
    },

    // Helper methods for filtering injuries
    filterInjuriesByWeek(week) {
      if (!week) return [];
      const weekDate = new Date(week);
      const weekStart = new Date(
        weekDate.getFullYear(),
        weekDate.getMonth(),
        weekDate.getDate(),
      );
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

      return this.injuries.filter((injury) => {
        const injuryDate = new Date(injury.created_date);
        return injuryDate >= weekStart && injuryDate < weekEnd;
      });
    },

    filterInjuriesByTeamAndPeriod(teamId, period) {
      if (!teamId || !period) return [];

      const [year, month] = period.split("-");
      const periodStart = new Date(year, month - 1, 1);
      const periodEnd = new Date(year, month, 0);

      return this.injuries.filter((injury) => {
        const injuryDate = new Date(injury.created_date);
        return (
          injury.teamId === teamId &&
          injuryDate >= periodStart &&
          injuryDate <= periodEnd
        );
      });
    },

    filterInjuriesByType(injuryType) {
      if (!injuryType) return [];
      return this.injuries.filter(
        (injury) => this.mapInjuryType(injury.injury_type) === injuryType,
      );
    },

    getInjuryBreakdownForTeam(teamInjuries) {
      const breakdown = {};
      teamInjuries.forEach((injury) => {
        const type = this.mapInjuryType(injury.injury_type);
        breakdown[type] = (breakdown[type] || 0) + 1;
      });
      return Object.entries(breakdown)
        .sort(([, a], [, b]) => b - a)
        .map(([type, count]) => ({ type, count }));
    },

    formatWeekForDisplay(week) {
      if (!week) return "";
      const date = new Date(week);
      return date.toLocaleDateString("en-GB", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatPeriodForDisplay(period) {
      if (!period) return "";
      const [year, month] = period.split("-");
      const date = new Date(year, month - 1);
      return date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },

    viewFullReport(drillDownData) {
      console.log("View full report requested for:", drillDownData);
      // Here you could navigate to a detailed report page
      // For now, show a comprehensive data export
      const reportData = {
        type: drillDownData.type,
        summary: {
          injuryType: drillDownData.injuryType,
          count: drillDownData.count,
          teamName: drillDownData.teamName,
          playerName: drillDownData.playerName,
          context: drillDownData.context,
        },
        injuries: drillDownData.injuries || [],
        generatedAt: new Date().toISOString(),
      };

      // Create a detailed report view
      const reportWindow = window.open("", "_blank");
      reportWindow.document.write(`
        <html>
          <head><title>Injury Report - ${drillDownData.injuryType || "Details"}</title></head>
          <body>
            <h1>Injury Report</h1>
            <pre>${JSON.stringify(reportData, null, 2)}</pre>
          </body>
        </html>
      `);
      reportWindow.document.close();
    },

    exportData(drillDownData) {
      console.log("Export data requested for:", drillDownData);
      // Create comprehensive export data
      const exportData = {
        exportType: "injury-drill-down",
        exportDate: new Date().toISOString(),
        summary: {
          type: drillDownData.type,
          injuryType: drillDownData.injuryType,
          count: drillDownData.count,
          teamName: drillDownData.teamName,
          playerName: drillDownData.playerName,
          context: drillDownData.context,
        },
        injuries: (drillDownData.injuries || []).map((injury) => ({
          playerId: injury.player_id,
          playerName: this.getPlayerName(injury.player_id),
          teamName: this.getTeamName(injury.teamId),
          injuryType: injury.injury_type,
          moreDetail: injury.more_detail,
          createdDate: injury.created_date,
          injuryStatus: injury.injury_status,
          returnTime: injury.return_time,
          activityAtTime: injury.activity_at_time,
          treatment: injury.treatment,
          diagnosis: injury.diagnosis,
        })),
        metadata: {
          totalInjuries: this.injuries.length,
          dateRange: {
            start: this.playingseasonStartDate,
            end: this.playingseasonEndDate,
          },
          selectedTeam: this.selectedTeamId,
          fullSeason: this.fullSeasonCheckBoxData,
        },
      };

      // Create and download the file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `injury-report-${drillDownData.type}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped lang="scss">
.academy-injury-summary {
  width: 100%;
}

.academy-injury-summary-section-reports {
  padding: 2rem;
  border: 1px solid #dddddd;
  margin: 1rem;
  border-radius: 4px;
}

.table-responsive {
  display: block;
  margin-bottom: 2rem;
  border-bottom: 1px dashed #ddd;
  padding-bottom: 2rem;
}

.academy-injury-summary h3 {
  margin-top: 36px;
  margin-bottom: 14px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #283593;
  border-left: 4px solid #1976d2;
  padding-left: 10px;
  background: #f5f7fa;
}

.injury-summary-table-scroll {
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(60, 60, 60, 0.06);
  background: #fafbfc;
  min-width: 700px;
  /* Responsive horizontal scroll for small screens */
  overflow-x: auto;
}

.injury-summary-table-scroll .b-table {
  margin-bottom: 0;
  min-width: 650px;
}

.b-table {
  background: #fafbfc;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(60, 60, 60, 0.06);
  margin-bottom: 32px;
  font-size: 0.98rem;
  width: 100%;
  /* Ensure table doesn't overflow on small screens */
  overflow-x: auto;
}

.b-table thead th,
.b-table td,
.b-table th {
  padding: 1rem !important;
  vertical-align: middle !important;
  text-align: left;
  white-space: nowrap;
  /* Prevent text from wrapping awkwardly */
}

@media (max-width: 900px) {
  .academy-injury-summary {
    padding: 12px 2vw;
  }

  .b-table {
    font-size: 0.93rem;
  }

  .academy-injury-summary h2 {
    font-size: 1.3rem;
  }

  .academy-injury-summary h3 {
    font-size: 1.05rem;
  }

  .injury-summary-table-scroll {
    min-width: 0;
    max-height: 300px;
    overflow-x: auto;
  }

  .injury-summary-table-scroll .b-table {
    min-width: 500px;
  }
}

@media (max-width: 600px) {

  .b-table thead th,
  .b-table td,
  .b-table th {
    padding: 0.5rem !important;
    font-size: 0.92rem;
  }

  .injury-summary-table-scroll {
    max-height: 220px;
  }

  .academy-injury-summary h2 {
    font-size: 1.05rem;
  }

  .academy-injury-summary h3 {
    font-size: 0.95rem;
  }
}

/* Optional: Make tables horizontally scrollable on very small screens */
@media (max-width: 500px) {

  .injury-summary-table-scroll,
  .b-table {
    overflow-x: auto;
    min-width: 0;
  }

  .b-table {
    font-size: 0.88rem;
  }
}

/* View Mode Controls */
.chart-view-controls {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.view-mode-section {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.view-mode-label {
  font-weight: 600;
  color: #283593;
  font-size: 1rem;
  min-width: 140px;
}

.view-mode-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.view-mode-btn {
  padding: 10px 20px;
  border: 2px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
}

.view-mode-btn:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}

.view-mode-btn.active {
  background: #007bff;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.view-mode-btn:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .view-mode-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .view-mode-label {
    min-width: auto;
  }

  .view-mode-buttons {
    width: 100%;
    justify-content: center;
  }

  .view-mode-btn {
    flex: 1;
    min-width: 70px;
  }
}

@media (max-width: 480px) {
  .chart-view-controls {
    padding: 15px;
  }

  .view-mode-buttons {
    flex-direction: column;
    width: 100%;
  }

  .view-mode-btn {
    width: 100%;
    padding: 12px;
  }
}

/* Clickable Table Styles */
.clickable-table {
  cursor: pointer;
}

.clickable-table tbody tr {
  transition: all 0.2s ease;
}

.clickable-table tbody tr:hover {
  background-color: #f8f9fa !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.clickable-table tbody tr:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Add subtle indicator that rows are clickable */
.clickable-table tbody tr::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: background 0.2s ease;
}

.clickable-table tbody tr:hover::after {
  background: #007bff;
}

/* Ensure table cells maintain proper styling */
.clickable-table tbody tr td {
  position: relative;
  border-color: #dee2e6 !important;
}

/* Add click feedback */
.clickable-table tbody tr:hover td {
  color: #007bff;
}

/* Special styling for total rows that shouldn't be clickable */
.clickable-table tbody tr[data-total="true"] {
  cursor: default;
  font-weight: bold;
  background-color: #f8f9fa;
}

.clickable-table tbody tr[data-total="true"]:hover {
  transform: none;
  box-shadow: none;
}

.clickable-table tbody tr[data-total="true"]:hover::after {
  background: transparent;
}

.clickable-table tbody tr[data-total="true"]:hover td {
  color: inherit;
}

/* Sortable Table Header Styles */
.b-table th[aria-sort] {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 30px !important;
}

.b-table th[aria-sort]:hover {
  background-color: #e9ecef !important;
}

.b-table th[aria-sort="none"]::after {
  content: "⇅";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.3;
  font-size: 12px;
}

.b-table th[aria-sort="ascending"]::after {
  content: "↑";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.8;
  font-size: 14px;
  color: #007bff;
}

.b-table th[aria-sort="descending"]::after {
  content: "↓";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.8;
  font-size: 14px;
  color: #007bff;
}

.b-table th[aria-sort]:focus {
  outline: 2px solid #007bff;
  outline-offset: -2px;
}

/* Improve header text visibility */
.b-table thead th {
  font-weight: 600;
  color: #283593;
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

/* Add visual feedback for sortable headers */
.b-table th[aria-sort]:active {
  background-color: #dee2e6 !important;
}

/* Season Comparison Styles */
.season-selector-wrapper {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.season-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.season-buttons {
  flex-grow: 1;
}

.season-buttons .btn-group {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.comparison-toggle-wrapper {
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
}

.comparison-toggle {
  white-space: nowrap;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border: 2px solid;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.comparison-toggle:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.comparison-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.comparison-active-indicator {
  padding: 0.75rem 1rem;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 0.375rem;
  color: #155724;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.season-comparison-dashboard {
  margin-bottom: 2rem;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.comparison-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #007bff;
}

.comparison-header h2 {
  color: #007bff;
  margin: 0;
  font-weight: 600;
}

.comparison-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.metric-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.metric-card h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
}

.metric-comparison {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
}

.season-metric {
  text-align: center;
}

.season-label {
  display: block;
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.metric-value {
  display: block;
  font-size: 1.8rem;
  font-weight: bold;
  color: #212529;
}

.trend-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.trend-better {
  color: #28a745;
  font-weight: 600;
}

.trend-worse {
  color: #dc3545;
  font-weight: 600;
}

.trend-same {
  color: #6c757d;
  font-weight: 600;
}

.side-by-side-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.season-column {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.season-column h4 {
  margin: 0 0 1.5rem 0;
  color: #007bff;
  font-weight: 600;
  text-align: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.comparison-summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-item {
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

@media (max-width: 768px) {
  .season-selector-wrapper {
    padding: 1rem;
  }

  .season-selector {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    gap: 1rem;
  }

  .comparison-toggle-wrapper {
    margin-left: 0;
    justify-content: center;
    width: 100%;
  }

  .comparison-toggle {
    width: 100%;
  }

  .comparison-metrics-grid {
    grid-template-columns: 1fr;
  }

  .side-by-side-comparison {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .metric-comparison {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .trend-indicator {
    margin: 0.5rem 0;
  }

  .season-buttons {
    width: 100%;
  }

  .season-buttons .btn-group {
    width: 100%;
    flex-direction: column;
  }

  .season-buttons .btn-group .btn {
    width: 100%;
    border-radius: 0.375rem !important;
    margin-bottom: 0.5rem;
  }

  .season-buttons .btn-group .btn:last-child {
    margin-bottom: 0;
  }

  .comparison-active-indicator {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .season-selector-wrapper {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .season-selector {
    gap: 0.75rem;
  }

  .season-buttons .btn-group .btn {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }

  .comparison-toggle {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }

  .comparison-active-indicator {
    font-size: 0.8rem;
    text-align: center;
    justify-content: center;
  }

  .season-comparison-dashboard {
    padding: 1rem;
  }

  .comparison-header h2 {
    font-size: 1.1rem;
  }

  .metric-card {
    padding: 1rem;
  }

  .metric-card h4 {
    font-size: 0.95rem;
  }

  .metric-value {
    font-size: 1.4rem;
  }

  .season-column {
    padding: 1rem;
  }

  .season-column h4 {
    font-size: 0.95rem;
  }

  .summary-item {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
}

/* RTP Badge Styles */
.rtp-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rtp-phase-1 {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

.rtp-phase-2 {
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
}

.rtp-phase-3 {
  background-color: #fffde7;
  color: #f9a825;
  border: 1px solid #fff59d;
}

.rtp-phase-4 {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.rtp-phase-5 {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.rtp-actions {
  display: flex;
  gap: 0.5rem;
}
</style>
